import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import {USE_LEGACY_TWEEN_GROUP} from 'components/customer/Preview3DCommon/lib/tween';
import {EasingFn} from './types';

// Shared incremental‑additive engine used by both the 'delta' and 'pulse'
// strategies.
//
// Each frame we compute the desired cumulative offset via
// `progressToOffset(progress)` and write only the change since the previous
// frame (`offset - applied`) onto the live axis. Because writes are pure
// additions, the tween never clobbers any other writer that touched the same
// axis in between. On completion we snap the accumulated offset to
// `finalOffset`, which fully determines the mode's semantic:
//   - `finalOffset = 0`  → self‑retracting excursion  (pulse)
//   - `finalOffset = Δ`  → permanent shift by Δ        (delta)
// The snap corrects for any residual from the last `onUpdate` not landing
// exactly on progress = 1.
export const runIncremental = ({
    mesh,
    property,
    axis,
    duration,
    delayBeforeStart,
    easing,
    progressToOffset,
    finalOffset,
}: {
    mesh: THREE.Group<THREE.Object3DEventMap> | THREE.Mesh;
    property: 'position' | 'rotation';
    axis: 'x' | 'y' | 'z';
    duration: number;
    delayBeforeStart: number;
    easing: EasingFn;
    progressToOffset: (progress: number) => number;
    finalOffset: number;
}): Promise<void> =>
    new Promise<void>((resolve) => {
        const state = {value: 0};
        let applied = 0;
        new TWEEN.Tween(state, USE_LEGACY_TWEEN_GROUP)
            .to({value: 1}, duration)
            .easing(easing)
            .delay(delayBeforeStart)
            .onUpdate(() => {
                const offset = progressToOffset(state.value);
                // eslint-disable-next-line security/detect-object-injection -- property and axis are validated by internal state of three.js
                mesh[property][axis] += offset - applied;
                applied = offset;
            })
            .onComplete(() => {
                const residual = finalOffset - applied;
                if (residual !== 0) {
                    // eslint-disable-next-line security/detect-object-injection -- property and axis are validated by internal state of three.js
                    mesh[property][axis] += residual;
                }
                resolve();
            })
            .start();
    });
